import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
    Home, Users, Bed as BedIcon, Search, 
    Plus, Activity, User, ShieldCheck, 
    ChevronRight, Loader2, X, Check,
    DollarSign, UserPlus
} from 'lucide-react';
import { toast } from 'react-hot-toast';

const WardManagement = () => {
    const navigate = useNavigate();
    const [wards, setWards] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [showAdmitModal, setShowAdmitModal] = useState(false);
    const [selectedBed, setSelectedBed] = useState(null);
    const [patientSearch, setPatientSearch] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [submitting, setSubmitting] = useState(false);

    const token = localStorage.getItem('token');
    const headers = { Authorization: `Bearer ${token}` };

    useEffect(() => {
        fetchWards();
    }, []);

    const fetchWards = async () => {
        try {
            setLoading(true);
            const response = await axios.get(`${window.location.protocol}//${window.location.hostname}/api/inpatient/wards`, { headers });
            setWards(response.data);
        } catch (err) {
            console.error('Fetch wards error:', err);
        } finally {
            setLoading(false);
        }
    };

    const handlePatientSearch = async (val) => {
        setPatientSearch(val);
        if (val.length < 2) {
            setSearchResults([]);
            return;
        }
        try {
            const response = await axios.get(`http://localhost:5000/api/patients?search=${val}`, { headers });
            setSearchResults(response.data);
        } catch (err) {
            console.error(err);
        }
    };

    const handleAdmit = async (patientId) => {
        try {
            setSubmitting(true);
            await axios.post(`${window.location.protocol}//${window.location.hostname}/api/inpatient/admissions`, {
                patientId,
                bedId: selectedBed.id,
                admissionDate: new Date(),
                diagnosis: 'Under observation'
            }, { headers });
            
            toast.success('Patient Admitted to Bed ' + selectedBed.bedNumber);
            setShowAdmitModal(false);
            setSelectedBed(null);
            setPatientSearch('');
            setSearchResults([]);
            fetchWards();
        } catch (err) {
            toast.error('Admission Failed');
        } finally {
            setSubmitting(false);
        }
    };

    const handleDischarge = async (bed) => {
        if (!window.confirm('Confirm Discharge for patient?')) return;
        try {
            // Find the active admission for this bed
            const admissionsRes = await axios.get(`${window.location.protocol}//${window.location.hostname}/api/inpatient/admissions`, { headers });
            const activeAd = admissionsRes.data.find(a => a.bedId === bed.id && a.status === 'admitted');
            
            if (activeAd) {
                await axios.put(`http://localhost:5000/api/inpatient/admissions/${activeAd.id}/discharge`, {}, { headers });
                toast.success('Patient Discharged Successfully');
                fetchWards();
            }
        } catch (err) {
            toast.error('Discharge Failed');
        }
    };

    if (loading) return (
        <div className="flex items-center justify-center min-h-screen bg-slate-50">
            <Loader2 className="w-10 h-10 animate-spin text-indigo-600" />
        </div>
    );

    return (
        <div className="p-4 md:p-8 bg-[#F8FAFC] min-h-screen font-sans text-slate-700">
            <div className="max-w-full mx-auto space-y-8">
                
                {/* Compact Header */}
                <header className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 bg-white p-6 rounded-[32px] border border-slate-100 shadow-xl shadow-slate-200/5 relative overflow-hidden">
                    <div className="absolute top-0 right-0 w-32 h-32 bg-indigo-50/20 rounded-full -mr-10 -mt-10 blur-2xl" />
                    <div className="flex items-center gap-4 relative z-10">
                        <div className="w-12 h-12 bg-slate-900 rounded-2xl flex items-center justify-center shadow-lg shadow-slate-200">
                            <Home className="w-6 h-6 text-white" />
                        </div>
                        <div>
                            <h1 className="text-xl font-black text-slate-800 tracking-tight leading-none uppercase">Ward Management</h1>
                            <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mt-2 leading-none">Live Facility Inventory Monitor</p>
                        </div>
                    </div>

                    <div className="flex items-center gap-2 w-full md:w-auto relative z-10">
                        <div className="relative flex-grow md:min-w-[320px]">
                            <Search className="absolute left-5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-300" />
                            <input 
                                type="text"
                                placeholder="SEARCH WARD IDENTITY..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full h-12 bg-slate-50 border border-slate-50 rounded-2xl pl-12 text-[10px] font-black outline-none focus:bg-white focus:border-indigo-100 transition-all uppercase tracking-wider"
                            />
                        </div>
                    </div>
                </header>

                {/* Ward Canvas Grid */}
                <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
                    {wards.filter(w => w.name.toLowerCase().includes(searchTerm.toLowerCase())).map(ward => (
                        <div key={ward.id} className="bg-white rounded-[40px] border border-slate-100 shadow-xl overflow-hidden flex flex-col group hover:translate-y-[-2px] transition-all duration-300">
                            
                            {/* Ward Header */}
                            <div className="p-6 pb-2 flex justify-between items-start bg-gradient-to-br from-white to-slate-50/30">
                                <div className="space-y-1">
                                    <h2 className="text-lg font-black text-slate-800 tracking-tight uppercase leading-none">{ward.name}</h2>
                                    <div className="flex items-center gap-2 mt-2">
                                        <span className="px-2 py-0.5 bg-indigo-50 text-indigo-600 rounded-lg text-[8px] font-black uppercase tracking-widest leading-none">{ward.category}</span>
                                        <span className="text-[9px] font-extrabold text-slate-400 flex items-center gap-1 border-l border-slate-100 pl-2 leading-none">
                                            <Users className="w-2.5 h-2.5" /> {ward.beds?.filter(b => b.isOccupied).length} / {ward.capacity} LOAD
                                        </span>
                                    </div>
                                </div>
                                <div className="text-right">
                                    <p className="text-[8px] font-black text-slate-300 uppercase tracking-widest mb-1 leading-none">NURSING STATUS</p>
                                    <div className="flex flex-col items-end gap-1 text-[9px] font-black text-indigo-500 uppercase tracking-tighter leading-none">
                                        <span className="hover:text-black cursor-pointer transition-colors">MT: {ward.matronName || 'PENDING'}</span>
                                        <span className="hover:text-black cursor-pointer transition-colors">SR: {ward.nurseInCharge || 'PENDING'}</span>
                                    </div>
                                </div>
                            </div>

                            {/* Bed Matrix */}
                            <div className="p-6 pt-4 flex-grow">
                                <div className="grid grid-cols-5 md:grid-cols-6 lg:grid-cols-8 gap-3">
                                    {ward.beds?.map(bed => (
                                        <div 
                                            key={bed.id}
                                            onClick={() => {
                                                if (bed.isOccupied) return;
                                                setSelectedBed(bed);
                                                setShowAdmitModal(true);
                                            }}
                                            className={`aspect-square rounded-2xl border-2 flex flex-col items-center justify-center gap-1 transition-all relative group/bed cursor-pointer overflow-hidden ${
                                                bed.isOccupied 
                                                ? 'bg-rose-50 border-rose-100 text-rose-500 shadow-rose-100' 
                                                : 'bg-emerald-50 border-emerald-100 text-emerald-500 hover:bg-emerald-500 hover:text-white hover:shadow-xl shadow-emerald-100'
                                            }`}
                                        >
                                            <BedIcon className={`w-5 h-5 ${bed.isOccupied ? 'animate-pulse' : ''}`} />
                                            <span className="text-[8px] font-black font-mono">{bed.bedNumber}</span>
                                            
                                            {/* Bed Tooltip/Details on Hover */}
                                            {bed.isOccupied ? (
                                                <div className="absolute inset-0 bg-rose-600 text-white p-3 flex flex-col justify-center opacity-0 group-hover/bed:opacity-100 transition-opacity">
                                                    <p className="text-[8px] font-black uppercase tracking-widest leading-tight">Occupied By</p>
                                                    <p className="text-[11px] font-black truncate">{bed.Patient?.firstName} {bed.Patient?.lastName}</p>
                                                    <button 
                                                        onClick={(e) => { e.stopPropagation(); handleDischarge(bed); }}
                                                        className="mt-2 py-1 bg-white/20 hover:bg-white text-rose-600 hover:text-rose-600 rounded-lg text-[8px] font-black uppercase tracking-widest transition-all"
                                                    >
                                                        Discharge
                                                    </button>
                                                </div>
                                            ) : (
                                                <div className="absolute top-1 right-1">
                                                    <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 group-hover/bed:bg-white" />
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Ward Footer */}
                            <div className="px-8 py-5 bg-slate-50 border-t border-slate-100 flex items-center justify-between">
                                <div className="flex items-center gap-4">
                                     <div className="flex items-center gap-2">
                                        <div className="w-2.5 h-2.5 rounded-full bg-rose-500" />
                                        <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Occupied</span>
                                     </div>
                                     <div className="flex items-center gap-2">
                                        <div className="w-2.5 h-2.5 rounded-full bg-emerald-500" />
                                        <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Vacant</span>
                                     </div>
                                </div>
                                <button 
                                    onClick={() => navigate(`/dashboard/inpatient/ward/${ward.id}`)}
                                    className="px-6 py-2.5 bg-white border border-slate-200 rounded-xl text-[10px] font-black text-slate-600 uppercase tracking-widest hover:bg-slate-900 hover:text-white hover:border-slate-900 transition-all flex items-center gap-2"
                                >
                                    Go to Ward <ChevronRight className="w-3.5 h-3.5" />
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Admission / Bed Assignment Modal */}
            {showAdmitModal && selectedBed && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm px-4">
                    <div className="bg-white rounded-[48px] w-full max-w-xl shadow-2xl relative overflow-hidden animate-in zoom-in-95 duration-300">
                        <div className="bg-slate-900 p-10 flex justify-between items-center text-white relative">
                             <div className="absolute -top-10 -right-10 w-40 h-40 bg-white/10 rounded-full blur-3xl" />
                             <div className="relative z-10 flex items-center gap-5">
                                 <div className="w-14 h-14 bg-white/10 rounded-2xl flex items-center justify-center backdrop-blur-md">
                                     <UserPlus className="w-7 h-7 text-white" />
                                 </div>
                                 <div>
                                     <h2 className="text-2xl font-black tracking-tight leading-tight">Admit Patient</h2>
                                     <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mt-1">Assigning to Bed {selectedBed.bedNumber}</p>
                                 </div>
                             </div>
                             <button onClick={() => setShowAdmitModal(false)} className="p-3 bg-white/10 hover:bg-white/20 rounded-2xl transition-all">
                                 <X className="w-6 h-6" />
                             </button>
                        </div>

                        <div className="p-10 space-y-8">
                            <div className="bg-slate-50 p-6 rounded-3xl border border-slate-100 flex justify-between items-center">
                                <div className="flex items-center gap-4">
                                    <div className="w-10 h-10 rounded-xl bg-white flex items-center justify-center shadow-sm text-indigo-600">
                                        <DollarSign className="w-5 h-5" />
                                    </div>
                                    <div>
                                        <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Daily Bed Rate</p>
                                        <p className="text-lg font-black text-slate-800">${parseFloat(selectedBed.price).toLocaleString()}</p>
                                    </div>
                                </div>
                                <div className="text-right">
                                    <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Facility Level</p>
                                    <p className="text-xs font-black text-indigo-600 uppercase tracking-widest">Premium Care</p>
                                </div>
                            </div>

                            <div className="space-y-4">
                                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-4">Search Patient to Admit</label>
                                <div className="relative">
                                    <Search className="absolute left-6 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-300" />
                                    <input 
                                        type="text"
                                        placeholder="Type Name, Email or ID..."
                                        value={patientSearch}
                                        onChange={(e) => handlePatientSearch(e.target.value)}
                                        className="w-full h-16 bg-white border-2 border-slate-100 rounded-3xl pl-16 text-sm font-black outline-none focus:border-indigo-600 transition-all shadow-sm"
                                    />
                                </div>

                                {/* Patient Results Dropdown */}
                                {searchResults.length > 0 && (
                                    <div className="bg-white border-2 border-slate-100 rounded-[32px] max-h-[300px] overflow-auto divide-y divide-slate-50 shadow-2xl animate-in fade-in slide-in-from-top-2">
                                        {searchResults.map(p => (
                                            <div 
                                                key={p.id}
                                                onClick={() => handleAdmit(p.id)}
                                                className="p-5 flex justify-between items-center hover:bg-slate-50 cursor-pointer transition-all group"
                                            >
                                                <div className="flex items-center gap-4">
                                                    <div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center text-[10px] font-black font-mono text-slate-500 uppercase tracking-widest group-hover:bg-indigo-600 group-hover:text-white transition-all">P{p.id}</div>
                                                    <div>
                                                        <h4 className="font-black text-slate-800 text-sm tracking-tight">{p.firstName} {p.lastName}</h4>
                                                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{p.email}</p>
                                                    </div>
                                                </div>
                                                <div className="w-10 h-10 rounded-full border border-slate-100 flex items-center justify-center text-slate-300 group-hover:border-indigo-600 group-hover:text-indigo-600 transition-all">
                                                    <ChevronRight className="w-5 h-5" />
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>

                            <button className="w-full py-5 bg-slate-100 text-slate-400 rounded-3xl font-black text-[12px] uppercase tracking-[0.2em] transition-all cursor-not-allowed">
                                Finalize Admission
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default WardManagement;
